import { SuggestedChange, SyncUser, User, UserRolePermission, UserType } from '@schooly/api';
import { Genders, Languages, SuggestedChangeDataSet } from '@schooly/constants';

import { hasFieldValue } from '../../../components/ui/Input/utils';
import {
  getUserAddress,
  getUserFullNameWithTitle,
  getUserNationalitiesList,
  getUserTelephonesList,
} from '../../../helpers/users';
import formatDate from '../../../utils/formatDate';

export const getSuggestedChangeTypeId = (
  dataSet?: SuggestedChangeDataSet,
  fieldName?: keyof SyncUser,
): string => {
  const dataSetMap: Record<SuggestedChangeDataSet, string> = {
    [SuggestedChangeDataSet.Address]: 'peopleDetail-Address',
    [SuggestedChangeDataSet.Name]: 'people-Name',
    [SuggestedChangeDataSet.Email]: 'people-Email',
    [SuggestedChangeDataSet.Nationality]: 'peopleDetail-Nationality-plural',
    [SuggestedChangeDataSet.Telephones]: 'peopleDetail-PhoneNumber-plural',
  };

  const userFieldMap: Partial<Record<keyof SyncUser, string>> = {
    known_as: 'peopleDetail-PreferredName',
    email: 'people-Email',
    date_of_birth: 'peopleDetail-DateOfBirth',
    gender: 'peopleDetail-Gender',
    language: 'peopleDetail-PrimaryLanguage',
    other_languages_spoken: 'peopleDetail-AdditionalLanguage-plural',
  };

  const defaultValue = '-';

  if (fieldName) {
    return userFieldMap[fieldName] || defaultValue;
  }

  if (dataSet) {
    return dataSetMap[dataSet];
  }

  return defaultValue;
};

const getChangeValueByFieldName = (
  fieldName: keyof SyncUser,
  relatedChanges: Pick<SuggestedChange, 'new_value'>,
) => {
  const getTextFromEnum = (
    e: {
      [id: number]: string;
    },
    newValue: string,
  ) => {
    if (newValue == null) return undefined;
    return hasFieldValue(Number(newValue)) ? e[Number(newValue)] : undefined;
  };

  const map: Partial<Record<keyof SyncUser, (newValue: string | number[]) => string | undefined>> =
    {
      known_as: (newValue) => newValue as string,
      date_of_birth: (newValue) => formatDate(newValue as string),
      gender: (newValue) => getTextFromEnum(Genders, newValue as string),
      language: (newValue) => getTextFromEnum(Languages, newValue as string),
      email: (newValue) => newValue as string,
      other_languages_spoken: (newValue) => {
        if (typeof newValue === 'number') {
          return Languages[newValue] ?? '';
        }

        try {
          return (newValue as number[]).map((lang) => Languages[lang]).join(', ');
        } catch (_error) {
          return undefined;
        }
      },
    };

  const getValue = map[fieldName] || (() => undefined);

  return getValue(relatedChanges.new_value);
};

const getChangeValueByDataSet = (
  dataSet: SuggestedChangeDataSet,
  { field, new_value }: Pick<SuggestedChange, 'field' | 'new_value'>,
  user: SyncUser,
) => {
  const map: Record<SuggestedChangeDataSet, (u: User) => string | undefined> = {
    [SuggestedChangeDataSet.Email]: (u) => u.email || undefined,
    [SuggestedChangeDataSet.Address]: (u) => getUserAddress(u),
    [SuggestedChangeDataSet.Name]: (u) => getUserFullNameWithTitle(u),
    [SuggestedChangeDataSet.Nationality]: (u) => getUserNationalitiesList(u),
    [SuggestedChangeDataSet.Telephones]: (u) => getUserTelephonesList(u),
  };

  const userCopy = user && JSON.parse(JSON.stringify(user));
  userCopy[field] = new_value;

  return map[dataSet](userCopy);
};

export const getChangeValue = (
  dataSet?: SuggestedChangeDataSet,
  fieldName?: keyof SyncUser,
  relatedChanges?: Pick<SuggestedChange, 'field' | 'new_value' | 'current_value_info'>,
  user?: SyncUser,
): [string, string] => {
  const defaultValue = '';

  if ((!dataSet && !fieldName) || !relatedChanges || !user) {
    return [defaultValue, defaultValue];
  }

  let fromValue, toValue;

  const currentValue = relatedChanges.current_value_info?.current_value;

  if (fieldName) {
    fromValue = currentValue
      ? getChangeValueByFieldName(fieldName, {
          new_value: currentValue,
        })
      : undefined;
    toValue = getChangeValueByFieldName(fieldName, relatedChanges);
  }

  if (dataSet) {
    fromValue = currentValue
      ? getChangeValueByDataSet(
          dataSet,
          {
            field: relatedChanges.field,
            new_value: currentValue,
          },
          user,
        )
      : undefined;
    toValue = getChangeValueByDataSet(dataSet, relatedChanges, user);
  }

  return [fromValue || defaultValue, toValue || defaultValue];
};

export const getUserNotificationTextId = (isCurrentUserUpdated: boolean, value: string) => {
  const action = value ? 'Updated' : 'Deleted';

  let pronoun = '';
  if (isCurrentUserUpdated) {
    pronoun = 'Their';
  }

  return `confirmation-User${action}${pronoun}Profile`;
};

export const getFieldNameBelongsToSet = (
  fieldName: keyof User,
  dataSet: SuggestedChangeDataSet,
) => {
  const map: Record<SuggestedChangeDataSet, (field: string) => boolean> = {
    [SuggestedChangeDataSet.Email]: (f) => ['email'].includes(f),
    [SuggestedChangeDataSet.Name]: (f) => ['title', 'given_name', 'last_name'].includes(f),
    [SuggestedChangeDataSet.Address]: (f) =>
      ['address_line_1', 'address_line_2', 'city', 'region', 'zip_code', 'country'].includes(f),
    [SuggestedChangeDataSet.Nationality]: (f) => ['nationality', 'other_nationalities'].includes(f),
    [SuggestedChangeDataSet.Telephones]: (f) => ['telephone', 'other_telephones'].includes(f),
  };

  return map[dataSet](fieldName);
};

export const getRelatedChanges = (
  suggestedChanges: SuggestedChange[],
  dataSet?: SuggestedChangeDataSet,
  fieldName?: keyof SyncUser,
) => {
  if (fieldName) {
    return suggestedChanges.find(({ field }) => field === fieldName);
  }
  if (dataSet) {
    return suggestedChanges.find(({ field }) => getFieldNameBelongsToSet(field, dataSet));
  }
  return;
};

export const hasPermissionForAcceptChanges = (
  userType: UserType,
  permissions?: UserRolePermission[],
) => {
  switch (userType) {
    case 'student':
      return permissions?.includes('student_manager');
    case 'parent':
      return permissions?.includes('parent_manager');
    case 'staff':
      return permissions?.includes('staff_manager');
    default:
      return false;
  }
};
